// src/pages/LavorazioniModifica.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { useNavigate } from 'react-router-dom';
import { Card, TableCell, TableHeadCell } from '../components/ui';

const API_BASE = '/api';

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatUserLabel = (u) => {
  if (!u) return 'utente';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  if (full) return full;
  return u.username || 'utente';
};

const formatInternalCode = (order, iterationOverride) => {
  if (!order) return '-';

  const base =
    order.internalCode ||
    order.codiceInterno ||
    (order.workOrder && order.workOrder.internalCode) ||
    '-';

  const iter =
    typeof iterationOverride === 'number'
      ? iterationOverride
      : typeof order.iteration === 'number'
      ? order.iteration
      : typeof order.workOrder?.iteration === 'number'
      ? order.workOrder.iteration
      : 0;

  return iter > 0 ? `${base}-${iter}` : base;
};

const getStatusInfo = (order) => {
  const status = order.status || 'in_corso';
  const iter =
    typeof order.iteration === 'number' ? order.iteration : 0;

  if (status === 'chiusa') {
    return {
      label: 'Terminata',
      className: 'bg-gray-100 text-gray-700 border border-gray-300',
    };
  }

  if (status === 'sospesa') {
    return {
      label: 'Sospesa',
      className: 'bg-amber-100 text-amber-800 border border-amber-200',
    };
  }

  if (
    order.assignedTo?.nome === 'Da' &&
    order.assignedTo?.cognome === 'Assegnare'
  ) {
    return {
      label: 'Da assegnare',
      className: 'bg-orange-100 text-orange-800 border border-orange-200',
    };
  }

  if (status === 'in_corso') {
    if (iter > 0) {
      return {
        label: 'Riassegnata',
        className:
          'bg-purple-100 text-purple-800 border border-purple-200',
      };
    }
    return {
      label: 'Attiva',
      className:
        'bg-emerald-100 text-emerald-800 border border-emerald-200',
    };
  }

  return {
    label: status,
    className: 'bg-gray-100 text-gray-700 border border-gray-300',
  };
};

const LavorazioniModifica = () => {
  const [myRole, setMyRole] = useState(localStorage.getItem('role') || null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [clientUsers, setClientUsers] = useState([]); 
  const [editCustomerUserId, setEditCustomerUserId] = useState('');

  const [orders, setOrders] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);

  const [calendarFrom, setCalendarFrom] = useState('');
  const [calendarTo, setCalendarTo] = useState('');

  // EDIT MODAL
  const [editOrder, setEditOrder] = useState(null);
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editPlace, setEditPlace] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editDates, setEditDates] = useState([]);
  const [editEquipmentType, setEditEquipmentType] = useState('');
  const [editEquipmentMaker, setEditEquipmentMaker] = useState('');
  const [editEquipmentModel, setEditEquipmentModel] = useState('');
  const [editEquipmentSerial, setEditEquipmentSerial] = useState('');
  const [editEquipmentInventory, setEditEquipmentInventory] = useState('');
  const [editEquipmentPresidio, setEditEquipmentPresidio] = useState('');
  const [editPreventivoStatus, setEditPreventivoStatus] = useState('');
  const [editOrderStatus, setEditOrderStatus] = useState(''); // 🔹 nuovo stato
  const [editConsuntivoStatus, setEditConsuntivoStatus] = useState('');
  const [editMaterialeArrivato, setEditMaterialeArrivato] = useState('');

  const [editRifPreventivo, setEditRifPreventivo] = useState('');
  const [editRifOrdineCliente, setEditRifOrdineCliente] = useState('');
  const [editManutenzioneConPreventivo, setEditManutenzioneConPreventivo] = useState(false);
  
  const [editImportoPreventivo, setEditImportoPreventivo] = useState('');
  

  // DELETE MODAL
  const [deleteOrder, setDeleteOrder] = useState(null);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // FILTRI tabella (stessi di LavorazioniInCorso)
  const [filterText, setFilterText] = useState('');
  const [filterPreventivo, setFilterPreventivo] = useState('tutti');
  const [filterOrdine, setFilterOrdine] = useState('tutti');


  const navigate = useNavigate();
  const isManager =
    myRole === 'admin' || myRole === 'segreteria' || myRole === 'gestione';

  const loadAssignableUsers = async () => {
    try {
      const res = await authFetch(`${API_BASE}/users`);
      if (!res.ok) return;
      const data = await res.json();
      const allowedRoles = ['admin', 'gestione', 'tecnico', 'Tecnico'];
      setAssignableUsers((data || []).filter((u) => allowedRoles.includes(u.role)));
      setClientUsers((data || []).filter((u) => u.role === 'cliente'));
    } catch (err) {
      console.error('Errore caricamento utenti:', err);
    }
  };

  const loadOrders = async () => {
    try {
      const res = await authFetch(`${API_BASE}/work/orders`);
      if (!res.ok) return;
      const data = await res.json();

      // 1) escludo chiuse e sospese
      const activeOnly = (data || []).filter(
        (o) => o.status !== 'chiusa' && o.status !== 'sospesa'
      );

      // 2) raggruppo per codice interno base
      const grouped = {};
      for (const o of activeOnly) {
        const baseCode = o.internalCode;
        if (!grouped[baseCode]) grouped[baseCode] = [];
        grouped[baseCode].push(o);
      }

      // 3) per ogni codice interno tengo solo l'iterazione più alta
      const filtered = Object.values(grouped).map((list) => {
        return list.reduce((max, item) => {
          const it = typeof item.iteration === 'number' ? item.iteration : 0;
          const maxIt = typeof max.iteration === 'number' ? max.iteration : 0;
          return it > maxIt ? item : max;
        });
      });

      // ordino per iterazione decrescente (più "giri" in alto)
      filtered.sort((a, b) => {
        const ia = a.iteration || 0;
        const ib = b.iteration || 0;
        return ib - ia;
      });

      setOrders(filtered);
    } catch (err) {
      console.error('Errore caricamento lavorazioni:', err);
    }
  };

  const loadCalendarEvents = async () => {
    try {
      const params = new URLSearchParams();
      if (calendarFrom) params.append('from', calendarFrom);
      if (calendarTo) params.append('to', calendarTo);
      const res = await authFetch(`${API_BASE}/work/calendar?${params.toString()}`);
      if (!res.ok) return;
      const data = await res.json();
      setCalendarEvents(data || []);
    } catch (err) {
      console.error('Errore calendario:', err);
    }
  };

  useEffect(() => {
    const storedRole = localStorage.getItem('role');
    if (storedRole) setMyRole(storedRole);
    loadAssignableUsers();
    loadOrders();
    loadCalendarEvents();
  }, []);

  // ========= EDIT =========

  const openEditModal = (o) => {
    setEditOrder(o);
    setEditCompanyName(o.companyName || '');
    setEditCustomerUserId(o.customerUserId || '');
    setEditPlace(o.place || '');
    setEditDescription(o.description || '');
    setEditEquipmentType(o.equipmentType || '');
    setEditEquipmentMaker(o.equipmentMaker || '');
    setEditEquipmentModel(o.equipmentModel || '');
    setEditEquipmentSerial(o.equipmentSerial || '');
    setEditEquipmentInventory(o.equipmentInventory || '');
    setEditEquipmentPresidio(o.equipmentPresidio || '');
    setEditPreventivoStatus(o.preventivoStatus || '');
    setEditOrderStatus(o.orderStatus || ''); // 🔹 inizializza stato ordine
    setEditRifPreventivo(o.rifPreventivo || '');
    setEditManutenzioneConPreventivo(!!o.manutenzioneConPreventivo);
    setEditConsuntivoStatus(o.consuntivoStatus || '');
    setEditMaterialeArrivato(o.materialeArrivato || '');
    setEditRifOrdineCliente(o.billing?.riferimentiOrdineCliente || o.rifOrdineCliente || '' );
    setEditImportoPreventivo(
      o.billing?.importoPreventivo !== undefined && o.billing?.importoPreventivo !== null
        ? String(o.billing.importoPreventivo)
        : ''
    );

    const events = o.events || [];
    const lastEvent = events.length > 0 ? events[events.length - 1] : null;

    const mainAssigned =
      lastEvent && lastEvent.assignedTo
        ? lastEvent.assignedTo._id
        : o.assignedTo?._id || '';

    setEditAssignedTo(mainAssigned);

    setEditDates(
      events.map((e) =>
        e.date ? new Date(e.date).toISOString().slice(0, 10) : ''
      )
    );
  };

  const closeEditModal = () => {
    setEditOrder(null);
    setEditCompanyName('');
    setEditCustomerUserId('');
    setEditPlace('');
    setEditDescription('');
    setEditAssignedTo('');
    setEditDates([]);
    setEditEquipmentType('');
    setEditEquipmentMaker('');
    setEditEquipmentModel('');
    setEditEquipmentSerial('');
    setEditEquipmentInventory('');
    setEditEquipmentPresidio('');
    setEditPreventivoStatus('');
    setEditOrderStatus(''); // 🔹 reset
    setEditConsuntivoStatus('');
    setEditMaterialeArrivato('');
    setEditRifPreventivo('');
    setEditManutenzioneConPreventivo(false);
    setEditRifOrdineCliente('');
    setEditImportoPreventivo('');
  };

  const handleEditDateChange = (index, value) => {
    const updated = [...editDates];
    updated[index] = value;
    setEditDates(updated);
  };

  const addEditDateField = () => setEditDates((prev) => [...prev, '']);
  const removeEditDateField = (index) => {
    if (editDates.length === 1) return;
    setEditDates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editOrder) return;

    const cleanDates = editDates.map((d) => d.trim()).filter(Boolean);

    // 🔴 Se sto assegnando la lavorazione a qualcuno,
    // devo avere almeno una data intervento
    if (editAssignedTo && cleanDates.length === 0) {
      setError(
        'Per assegnare la lavorazione a un tecnico devi inserire almeno una data di intervento.'
      );
      return;
    }

    if (
      editOrder.type === 'intervento' &&
      editPreventivoStatus === 'A consuntivo' &&
      !editConsuntivoStatus
    ) {
      setError('Se selezioni "A consuntivo" devi indicare "Si" o "No".');
      return;
    }

    if (
      editOrder.type === 'intervento' &&
      editOrderStatus === 'Ordine ricevuto' &&
      !editRifOrdineCliente.trim()
    ) {
      setError('Se lo stato ordine è "Ordine ricevuto" devi inserire il Rif. ordine.');
      return;
    }

    const payload = {
      companyName: editCompanyName.trim() || undefined,
      customerUserId: editCustomerUserId || null,
      place: editPlace.trim() || undefined,
      description: editDescription.trim() || undefined,
      assignedTo: editAssignedTo || undefined,
      dates: cleanDates,
      equipmentType: editEquipmentType.trim() || undefined,
      equipmentMaker: editEquipmentMaker.trim() || undefined,
      equipmentModel: editEquipmentModel.trim() || undefined,
      equipmentSerial: editEquipmentSerial.trim() || undefined,
      equipmentInventory: editEquipmentInventory.trim() || undefined,
      equipmentPresidio: editEquipmentPresidio.trim() || undefined,
      preventivoStatus:
        editOrder.type === 'intervento'
          ? editPreventivoStatus || undefined
          : undefined,

      materialeArrivato:
        editOrder.type === 'intervento'
          ? editMaterialeArrivato || undefined
          : undefined,

      consuntivoStatus:
        editOrder.type === 'intervento' && editPreventivoStatus === 'A consuntivo'
          ? editConsuntivoStatus || undefined
          : undefined,

      rifPreventivo:
        editOrder.type === 'intervento' ||
        (editOrder.type === 'manutenzione' && editManutenzioneConPreventivo)
          ? (editRifPreventivo || '').trim()
          : '',

      manutenzioneConPreventivo:
        editOrder.type === 'manutenzione' ? !!editManutenzioneConPreventivo : false,

      orderStatus:
        editOrder.type === 'intervento'
          ? editOrderStatus || undefined
          : undefined,

      rifOrdineCliente:
          editOrder.type === 'intervento' && editOrderStatus === 'Ordine ricevuto'
            ? editRifOrdineCliente.trim() || undefined
            : undefined,

      importoPreventivo:
        (editOrder.type === 'intervento' ||
          (editOrder.type === 'manutenzione' && editManutenzioneConPreventivo)) &&
        String(editImportoPreventivo || '').trim() !== ''
          ? String(editImportoPreventivo).trim()
          : undefined,
    };

    try {
      const res = await authFetch(`${API_BASE}/work/${editOrder._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setError(
          data?.error || "Errore durante l'aggiornamento della lavorazione."
        );
        return;
      }

      setMessage('Lavorazione aggiornata con successo.');
      setError('');
      closeEditModal();
      loadOrders();
      loadCalendarEvents();
    } catch (err) {
      console.error('Errore aggiornamento lavorazione:', err);
      setError('Errore nella richiesta al server.');
    }
  };

  // ========= DELETE (POPUP) =========

  const openDeleteModal = (order) => {
    setDeleteOrder(order);
    setMessage('');
    setError('');
  };

  const closeDeleteModal = () => {
    setDeleteOrder(null);
  };

  const handleDeleteOrder = async () => {
    if (!deleteOrder) return;

    try {
      const res = await authFetch(`${API_BASE}/work/${deleteOrder._id}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setError(
          data?.error || "Errore durante l'eliminazione della lavorazione."
        );
        return;
      }
      setMessage('Lavorazione eliminata con successo.');
      setError('');
      closeDeleteModal();
      loadOrders();
      loadCalendarEvents();
    } catch (err) {
      console.error('Errore eliminazione lavorazione:', err);
      setError('Errore nella richiesta al server.');
    }
  };

  const tableOrders = useMemo(() => {
    const text = filterText.trim().toLowerCase();

  const getPrevPriority = (s) => {
    if (s === 'Preventivo da fare') return 4;
    if (s === 'A consuntivo') return 3;
    if (s === 'Contratto') return 2;
    if (s === 'Preventivo fatto') return 1;
    return 0;
  };

    const getOrdPriority = (s) => {
      if (s === 'Ordine non ricevuto') return 2;
      if (s === 'Ordine ricevuto') return 1;
      return 0;
    };

    return (orders || [])
      .filter((o) => {
        const target = [
          formatInternalCode(o),
          o.externalCode || o.codiceEsterno,
          o.companyName || o.cliente,
          o.place || o.luogo,
          o.equipmentModel || o.modello,
          o.equipmentSerial || o.seriale || o.matricola,
          o.equipmentInventory || o.inventario,
          o.equipmentPresidio || o.presidio,
          o.materialeArrivato,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (text && !target.includes(text)) return false;

        // NB: filtri preventivo/ordine hanno senso solo per type === 'intervento'
        if (filterPreventivo !== 'tutti') {
          if (o.type !== 'intervento') return false;
          if ((o.preventivoStatus || '') !== filterPreventivo) return false;
        }

        if (filterOrdine !== 'tutti') {
          if (o.type !== 'intervento') return false;
          if ((o.orderStatus || '') !== filterOrdine) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const pA = getPrevPriority(a.preventivoStatus);
        const pB = getPrevPriority(b.preventivoStatus);
        if (pA !== pB) return pB - pA;

        const oA = getOrdPriority(a.orderStatus);
        const oB = getOrdPriority(b.orderStatus);
        if (oA !== oB) return oB - oA;

        const dA = new Date(a.receivedAt || 0).getTime();
        const dB = new Date(b.receivedAt || 0).getTime();
        return dB - dA;
      });
  }, [orders, filterText, filterPreventivo, filterOrdine]);


  if (!isManager) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-100 px-4">
        <Card className="shadow-md p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Accesso non autorizzato
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Solo <strong>Admin</strong>, <strong>Segreteria</strong> e{' '}
            <strong>Gestione</strong> possono modificare le lavorazioni.
          </p>
          <button
            onClick={() => navigate('/lavorazioni')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
          >
            Torna alle lavorazioni
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* header */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Modifica lavorazioni
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Gestisci le lavorazioni esistenti e consulta il calendario globale.
            </p>
          </div>
          <button
            onClick={() => navigate('/lavorazioni')}
            className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            Indietro
          </button>
        </Card>

        {(message || error) && (
          <Card className="shadow-sm px-4 py-3 md:px-6">
            {message && (
              <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-1">
                {message}
              </p>
            )}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
          </Card>
        )}

        {/* tabella lavorazioni */}
        <Card className="shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                Lavorazioni esistenti
              </h2>
              <p className="text-[11px] md:text-xs text-gray-500">
                Solo lavorazioni attive / riassegnate (escluse chiuse e sospese).
              </p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-3">
            <div className="flex flex-col md:flex-row gap-2 md:items-center w-full">
              <input
                type="text"
                placeholder="Cerca per codice, cliente, luogo, modello..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full md:w-72 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-2">
                <select
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  value={filterPreventivo}
                  onChange={(e) => setFilterPreventivo(e.target.value)}
                >
                  <option value="tutti">Tutti i preventivi</option>
                  <option value="Preventivo da fare">Preventivo da fare</option>
                  <option value="Preventivo fatto">Preventivo fatto</option>
                  <option value="A consuntivo">A consuntivo</option>
                  <option value="Contratto">Contratto</option>
                </select>

                <select
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  value={filterOrdine}
                  onChange={(e) => setFilterOrdine(e.target.value)}
                >
                  <option value="tutti">Tutti gli ordini</option>
                  <option value="Ordine ricevuto">Ordine ricevuto</option>
                  <option value="Ordine non ricevuto">Ordine non ricevuto</option>
                </select>
              </div>
            </div>
          </div>


          {tableOrders.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nessuna lavorazione registrata al momento.
            </p>
          ) : (
            <>
              <p className="text-[11px] text-gray-400 mb-2 md:hidden">
                Scorri orizzontalmente la tabella per visualizzare tutti i dati.
              </p>
              <div className="overflow-x-auto">
                <div className="max-h-[520px] overflow-auto border border-gray-200 rounded-lg">
                  <table className="min-w-max w-full border-separate border-spacing-0 text-xs md:text-sm">
                    <colgroup>
                      <col style={{ width: 110 }} /><col style={{ width: 140 }} /><col style={{ width: 190 }} />
                    </colgroup>
                              
                  <thead className="sticky top-0 bg-gray-50 z-20">
                    <tr className="bg-gray-50 text-gray-700">
                      <TableHeadCell className="px-3 py-2 text-left bg-gray-50 md:sticky md:top-0 md:left-0 md:z-40">
                        Stato
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left bg-gray-50 md:sticky md:top-0 md:left-[110px] md:z-40">
                        Cod. interno
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left bg-gray-50 md:sticky md:top-0 md:left-[250px] md:z-40">
                        Cod. esterno
                      </TableHeadCell>                                
                      <TableHeadCell className="px-3 py-2 text-left">
                        Tipo lav.
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Manut. con prev
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Rif. preventivo
                      </TableHeadCell>  
                      <TableHeadCell className="px-3 py-2 text-left">
                        Preventivo
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Consuntivo
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Stato ordine
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Materiale arrivato
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Azienda / Cliente
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Luogo
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Tipo apparecchiatura
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Produttore
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Modello
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Seriale
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Inventario
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Presidio
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Assegnato a
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Date intervento
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Data ricezione
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Azioni
                      </TableHeadCell>
                    </tr>
                  </thead>
                  <tbody>
                    {tableOrders.map((o) => {
                      const events = o.events || [];
                      const lastEvent =
                        events.length > 0 ? events[events.length - 1] : null;

                      const dataInterventi =
                        events
                          .map((e) =>
                            e.date
                              ? new Date(e.date).toLocaleDateString('it-IT')
                              : ''
                          )
                          .filter(Boolean)
                          .join(', ') || '-';

                      const tecnicoName = lastEvent?.assignedTo
                        ? formatUserLabel(lastEvent.assignedTo)
                        : o.assignedTo
                        ? formatUserLabel(o.assignedTo)
                        : '-';

                      const statusInfo = getStatusInfo(o);
                      const preventivoLabel =
                        o.type === 'intervento'
                          ? o.preventivoStatus || '-'
                          : '-';

                      const orderStatusLabel =
                        o.type === 'intervento'
                          ? o.orderStatus || '-'
                          : '-';

                      return (
                        <tr
                          key={o._id}
                          className="border-b last:border-b-0 hover:bg-gray-50"
                        >
                          <TableCell className="px-3 py-2 align-top bg-white md:sticky md:left-0 md:z-30">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-[10px] font-semibold ${statusInfo.className}`}
                            >
                              {statusInfo.label}
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap bg-white md:sticky md:left-[110px] md:z-30">
                            {formatInternalCode(o)}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap bg-white md:sticky md:left-[250px] md:z-30">
                            {o.externalCode || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top capitalize">
                            {o.type || '—'}
                          </TableCell>
                          <TableCell className="px-2 py-1">

                        {o.type === 'manutenzione' && o.manutenzioneConPreventivo ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Sì
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          
                          <TableCell className="px-2 py-1">
                            {o.rifPreventivo ? (
                              <span className="font-mono">{o.rifPreventivo}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>

                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {preventivoLabel}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {o.preventivoStatus === 'A consuntivo' ? (o.consuntivoStatus || '—') : '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {o.type === 'intervento' ? (
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  orderStatusLabel === 'Ordine ricevuto'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : orderStatusLabel === 'Ordine non ricevuto'
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                                }`}
                              >
                                {orderStatusLabel}
                              </span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {o.type === 'intervento' ? (o.materialeArrivato || '—') : '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {o.companyName || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {o.place || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {o.equipmentType || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {o.equipmentMaker || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {o.equipmentModel || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {o.equipmentSerial || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {o.equipmentInventory || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {o.equipmentPresidio || '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {tecnicoName}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            {dataInterventi}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top whitespace-nowrap">
                            {formatDate(o.receivedAt)}
                          </TableCell>
                          <TableCell className="px-3 py-2 align-top">
                            <div className="flex flex-wrap gap-1">
                              <button
                                type="button"
                                onClick={() => openEditModal(o)}
                                className="px-3 py-1 rounded-lg bg-amber-500 text-white text-[11px] md:text-xs font-semibold hover:bg-amber-600 transition"
                              >
                                Modifica
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteModal(o)}
                                className="px-3 py-1 rounded-lg bg-red-600 text-white text-[11px] md:text-xs font-semibold hover:bg-red-700 transition"
                              >
                                Elimina
                              </button>
                            </div>
                          </TableCell>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div> 
              </div>
            </>
          )}
        </Card>

        {/* calendario globale */}
        <Card className="shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800">
              Calendario lavorazioni (globale)
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
              <div className="flex items-center gap-1">
                <span className="text-gray-600">Da:</span>
                <input
                  type="date"
                  value={calendarFrom}
                  onChange={(e) => setCalendarFrom(e.target.value)}
                  className="px-2 py-1 border rounded-lg focus:outline-none focus:ring-1 focus:ring-sky-500 text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-600">A:</span>
                <input
                  type="date"
                  value={calendarTo}
                  onChange={(e) => setCalendarTo(e.target.value)}
                  className="px-2 py-1 border rounded-lg focus:outline-none focus:ring-1 focus:ring-sky-500 text-xs"
                />
              </div>
              <button
                type="button"
                onClick={loadCalendarEvents}
                className="px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700"
              >
                Aggiorna calendario
              </button>
            </div>
          </div>

          {calendarEvents.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nessuna lavorazione trovata per il periodo selezionato.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full border-collapse text-xs md:text-sm">
                <colgroup>
                  <col style={{ width: 90 }} /><col style={{ width: 130 }} /><col style={{ width: 170 }} />
                </colgroup>
                <thead className="sticky top-0 z-40">

                  <tr className="bg-gray-50 text-gray-700">
                    <TableHeadCell className="px-3 py-2 text-left">
                      Data
                    </TableHeadCell>
                    <TableHeadCell className="px-3 py-2 text-left">
                      Cod. interno
                    </TableHeadCell>
                    <TableHeadCell className="px-3 py-2 text-left">
                      Tipo lav.
                    </TableHeadCell>
                    <TableHeadCell className="px-3 py-2 text-left">
                      Azienda
                    </TableHeadCell>
                    <TableHeadCell className="px-3 py-2 text-left">
                      Luogo
                    </TableHeadCell>
                    <TableHeadCell className="px-3 py-2 text-left">
                      Assegnato a
                    </TableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {calendarEvents.map((ev) => (
                    <tr
                      key={ev._id}
                      className="border-b last:border-b-0 hover:bg-gray-50"
                    >
                      <TableCell className="px-3 py-2">
                        {ev.date ? formatDate(ev.date) : '-'}
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        {formatInternalCode(ev.workOrder, ev.iteration)}
                      </TableCell>
                      <TableCell className="px-3 py-2 capitalize">
                        {ev.workOrder?.type || '-'}
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        {ev.workOrder?.companyName || '-'}
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        {ev.workOrder?.place || '-'}
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        {ev.assignedTo ? formatUserLabel(ev.assignedTo) : '-'}
                      </TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* MODAL MODIFICA - ora scrollabile */}
      {editOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-2xl shadow-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">
              Modifica lavorazione {formatInternalCode(editOrder)}
            </h3>
            <p className="text-[11px] text-gray-500 mb-3">
              Tipo: <span className="font-semibold">{editOrder.type}</span>{' '}
              &bull; Azienda: {editOrder.companyName || '-'} &bull; Luogo:{' '}
              {editOrder.place || '-'}
            </p>
            <form onSubmit={handleSubmitEdit} className="space-y-3 text-sm">
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Azienda / Cliente
                </label>
                <input
                  type="text"
                  value={editCompanyName}
                  onChange={(e) => setEditCompanyName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Account Cliente (opzionale)
                </label>
                <select
                  value={editCustomerUserId}
                  onChange={(e) => setEditCustomerUserId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="">— Nessun cliente —</option>
                  {clientUsers.map((c) => (
                    <option key={c._id} value={c._id}>
                      {(c.nome || '').trim() || c.username} ({c.matricola || 'C****'})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-gray-400">
                  Se selezioni un cliente, vedrà questa lavorazione nella sua dashboard.
                </p>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Luogo intervento
                </label>
                <input
                  type="text"
                  value={editPlace}
                  onChange={(e) => setEditPlace(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Descrizione
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y"
                />
              </div>

              {/* Dati apparecchiatura */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Dati apparecchiatura
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                  <div>
                    <span className="block text-[11px] text-gray-500 mb-1">
                      Tipo
                    </span>
                    <input
                      type="text"
                      value={editEquipmentType}
                      onChange={(e) => setEditEquipmentType(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] text-gray-500 mb-1">
                      Produttore
                    </span>
                    <input
                      type="text"
                      value={editEquipmentMaker}
                      onChange={(e) => setEditEquipmentMaker(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] text-gray-500 mb-1">
                      Modello
                    </span>
                    <input
                      type="text"
                      value={editEquipmentModel}
                      onChange={(e) => setEditEquipmentModel(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] text-gray-500 mb-1">
                      Seriale
                    </span>
                    <input
                      type="text"
                      value={editEquipmentSerial}
                      onChange={(e) => setEditEquipmentSerial(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] text-gray-500 mb-1">
                      Inventario
                    </span>
                    <input
                      type="text"
                      value={editEquipmentInventory}
                      onChange={(e) => setEditEquipmentInventory(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] text-gray-500 mb-1">
                      Presidio
                    </span>
                    <input
                      type="text"
                      value={editEquipmentPresidio}
                      onChange={(e) => setEditEquipmentPresidio(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>
              </div>

              {/* Stato preventivo + stato ordine se intervento */}
              {editOrder.type === 'intervento' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-gray-700 font-medium mb-1">
                      Stato preventivo
                    </label>
                      <select
                        value={editPreventivoStatus}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditPreventivoStatus(value);

                          if (value !== 'A consuntivo') {
                            setEditConsuntivoStatus('');
                          }
                        }}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                      >
                        <option value="">Seleziona stato preventivo</option>
                        <option value="Preventivo da fare">Preventivo da fare</option>
                        <option value="Preventivo fatto">Preventivo fatto</option>
                        <option value="A consuntivo">A consuntivo</option>
                        <option value="Contratto">Contratto</option>
                      </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-1">
                      Stato ordine
                    </label>
                    <select
                      value={editOrderStatus}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditOrderStatus(value);
                        if (value !== 'Ordine ricevuto') {
                          setEditRifOrdineCliente('');
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                    >
                      <option value="">(non modificare)</option>
                      <option value="Ordine ricevuto">Ordine ricevuto</option>
                      <option value="Ordine non ricevuto">
                        Ordine non ricevuto
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-1">
                      Materiale arrivato
                    </label>
                    <select
                      value={editMaterialeArrivato}
                      onChange={(e) => setEditMaterialeArrivato(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                    >
                      <option value="">(non modificare)</option>
                      <option value="Si">Sì</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                </div>
              )}

              {editOrder?.type === 'intervento' &&
                editOrderStatus === 'Ordine ricevuto' && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-1">
                      Rif. ordine
                    </label>
                    <input
                      type="text"
                      value={editRifOrdineCliente}
                      onChange={(e) => setEditRifOrdineCliente(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="Inserisci riferimento ordine cliente"
                    />
                  </div>
                )}

              {editOrder?.type === 'intervento' && editPreventivoStatus === 'A consuntivo' && (
                <div className="mt-3">
                  <label className="block text-gray-700 font-medium mb-1">
                    A consuntivo
                  </label>
                  <select
                    value={editConsuntivoStatus}
                    onChange={(e) => setEditConsuntivoStatus(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                  >
                    <option value="">Seleziona</option>
                    <option value="Si">Sì</option>
                    <option value="No">No</option>
                  </select>
                </div>
              )}

              {/* Manutenzione con preventivo (solo manutenzioni) */}
              {editOrder?.type === 'manutenzione' && (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Manutenzione da effettuare con preventivo?
                  </label>
              
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEditManutenzioneConPreventivo(true)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium ${
                        editManutenzioneConPreventivo
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Sì
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        setEditManutenzioneConPreventivo(false);
                        setEditRifPreventivo('');
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium ${
                        !editManutenzioneConPreventivo
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}

              {/* Rif. preventivo (intervento sempre / manutenzione solo se flag true) */}
              {(editOrder?.type === 'intervento' ||
                (editOrder?.type === 'manutenzione' && editManutenzioneConPreventivo)) && (
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Rif. preventivo</label>
                  <input
                    type="text"
                    value={editRifPreventivo}
                    onChange={(e) => setEditRifPreventivo(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    placeholder="Inserire se esistente"
                  />
                </div>
              )}


              {(editOrder?.type === 'intervento' ||
                (editOrder?.type === 'manutenzione' && editManutenzioneConPreventivo)) && (
                <div>
                  <label className="block text-gray-700 font-medium mb-1">
                    Importo preventivo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editImportoPreventivo}
                    onChange={(e) => setEditImportoPreventivo(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    placeholder="Es. 1250.00"
                  />
                </div>
              )}


              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Assegna a
                </label>
                <select
                  value={editAssignedTo}
                  onChange={(e) => setEditAssignedTo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="">(mantieni assegnatario attuale)</option>
                  {assignableUsers.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.nome || u.cognome
                        ? `${u.nome || ''} ${u.cognome || ''}`.trim()
                        : u.username}{' '}
                      ({u.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Giorni intervento / manutenzione
                </label>
                <p className="text-[11px] text-gray-400 mb-2">
                  Se modifichi le date e l&apos;assegnatario, verranno rigenerati gli
                  eventi per questa lavorazione.
                </p>
                <div className="space-y-2">
                  {editDates.map((d, index) => (
                    <div key={index} className="flex items-center gap-2 max-w-xs">
                      <input
                        type="date"
                        value={d}
                        onChange={(e) =>
                          handleEditDateChange(index, e.target.value)
                        }
                        required={!!editAssignedTo && index === 0}
                        className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                      />
                      {editDates.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEditDateField(index)}
                          className="px-2 py-1 rounded-lg text-xs bg-red-100 text-red-700 hover:bg-red-200"
                        >
                          −
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addEditDateField}
                  className="mt-2 inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                >
                  + Aggiungi data
                </button>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                >
                  Salva modifiche
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAZIONE */}
      {deleteOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-3 text-red-700">
              Elimina lavorazione {formatInternalCode(deleteOrder)}
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Sei sicuro di voler eliminare definitivamente questa lavorazione?
              L&apos;operazione non è reversibile.
            </p>

            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-4">
              <p>
                <strong>Azienda:</strong> {deleteOrder.companyName || '-'}
              </p>
              <p>
                <strong>Luogo:</strong> {deleteOrder.place || '-'}
              </p>
              <p>
                <strong>Date:</strong>{' '}
                {(deleteOrder.events || [])
                  .map((e) =>
                    e.date ? new Date(e.date).toLocaleDateString('it-IT') : ''
                  )
                  .filter(Boolean)
                  .join(', ') || '-'}
              </p>
              <p>
                <strong>Assegnato a:</strong>{' '}
                {(() => {
                  const evs = deleteOrder.events || [];
                  const lastEv =
                    evs.length > 0 ? evs[evs.length - 1] : null;
                  if (lastEv && lastEv.assignedTo) {
                    return formatUserLabel(lastEv.assignedTo);
                  }
                  return deleteOrder.assignedTo
                    ? formatUserLabel(deleteOrder.assignedTo)
                    : '-';
                })()}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleDeleteOrder}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
              >
                Conferma eliminazione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LavorazioniModifica;
